/**
 * Das Schliessen des Kommentarblatts — herausgelöst aus `CommentSheet.tsx`, aus demselben
 * Grund wie `handwritingSave.ts`: das Projekt hat keine Infrastruktur, um Komponenten in
 * Tests zu zeichnen (`vitest.config.ts` läuft mit `environment: 'node'`). Was nicht geprüft
 * werden kann, bleibt sonst kaputt.
 *
 * **Der Auftrag, 2026-09-24.** Der Nutzer kam am iPad aus dem Kommentarblatt nicht mehr
 * heraus. Ursache war eine *Absicht*: `handleClose` liess das Schreiben ungefangen werfen,
 * damit der Fehlschlag „nicht stillschweigend verschwindet". Zwei Dinge daran waren falsch.
 *
 * 1. ⛔ **Die Tür darf nie verschlossen sein.** `handleClose` hängt an allen Schliesswegen —
 *    Fertig-Knopf, Escape, Klick daneben. Ein Prüfer, der mitten in einer Prüfung in einem
 *    Blatt gefangen ist, kann nicht weiterbewerten; das ist teurer als jeder Schreibfehler.
 * 2. ⚠️ **Still war es trotzdem.** Die Ablehnung lief ins Leere: keine Einblendung, keine
 *    Zeile im Dateilog. Genau deshalb war am Gerät hinterher nicht herauszufinden, woran es
 *    lag.
 *
 * Also: melden **und** schliessen — dasselbe Muster wie in `annotate/AnnotateSheet.tsx`.
 */

export interface SchliessDeps {
  /** Schreibt die Handschriftseite; wirft, wenn das misslingt. */
  sichere: () => Promise<void>;
  /** Die Einblendung für den Prüfer. */
  toast: (schluessel: 'writeFailed') => void;
  /**
   * Das Dateilog — trägt die Ursache, die dem Toast fehlt. `stufe` bestimmt, auf welcher
   * Konsolenfunktion die Zeile am Kabel erscheint (s. `platform/appLog.ts`).
   */
  log: (text: string, stufe?: 'error') => Promise<void>;
  /** Das Blatt zumachen. */
  close: () => void;
}

/**
 * Schliesst das Blatt — **immer**, auch wenn das Sichern scheitert.
 *
 * ⚠️ **Wirft nie.** Auch nicht, wenn das Protokollieren selbst scheitert: ein Dateilog, das
 * nicht schreibbar ist, darf nicht der Grund sein, warum ein Blatt offen bleibt.
 */
export async function schliesseKommentarblatt(deps: SchliessDeps): Promise<void> {
  try {
    await deps.sichere();
  } catch (fehler) {
    deps.toast('writeFailed');
    try {
      await deps.log('Handschrift sichern fehlgeschlagen: ' + String(fehler), 'error');
    } catch {
      // Ohne Fang bliebe das Blatt genau an der Stelle zu, die es aufmachen soll.
    }
  } finally {
    deps.close();
  }
}
